import { CoreButtonView, CoreImageView, CoreTextView, type TCoreButtonStyleProps, type TCoreButtonViewProps, type TCoreTextStyleProps } from '@core/widgets';
import { TViewCommonOption, TWidget, TWidgetCommonOption } from '@libs/types';
import React, { MouseEvent, MouseEventHandler } from 'react';
import { TSelectorRegularData } from '../../../types/data';
import { TRowOption, TSelectorRegularOption } from '../../../types/options';
import { TextStyle } from '../../../types/styles';
import { getHref } from '../../Image/imageUtils';
import { VisualView } from '../../Image/VisualView';
import { Tag } from '../Tag/Tag';
import styles from './styles/selectorExtra.module.css';

export type TSelectorExtraViewProps = {
    widget?: TWidget<TSelectorRegularData, TRowOption & TSelectorRegularOption>;
    uid?: TWidget<TWidgetCommonOption>['uid'];
    title?: string;
    subtitle?: string;
    metaText?: string;
    metaVariant?: TRowOption['metaVariant'];
    metaHierarchy?: TRowOption['metaHierarchy'];
    iconName?: string;
    defaultIconName?: string;
    flagIcon?: string;
    flagIconSmall?: string;
    avatar?: string;
    iconHierarchy?: TRowOption['iconHierarchy'];
    iconVariant?: TRowOption['iconVariant'];
    isSelected?: boolean;
    curIso?: string;
    curIsoIcon?: boolean;
    margin?: TWidgetCommonOption['margin'];
    isDisabled?: boolean;
    isHide?: boolean;
    handleClick: ((e: MouseEvent) => void) | MouseEventHandler<HTMLButtonElement>;
    parentStyleNames?: TViewCommonOption['parentStyleNames'];
};

const CONTAINER_STYLE_PROPS: TCoreButtonStyleProps = {
    main: 'var(--color-white)',
    onMain: 'var(--color-black)',
    fillContainer: true,
    borderRadius: 0,
    cursorDisabled: 'default',
    textAlign: 'start',
    gap: 16,
    disabled: {
        main: 'transparent',
        onMain: 'var(--color-grey_40_lc)',
    },
    hovered: {
        main: 'var(--color-grey_8_lc)',
    },
    pressed: {
        main: 'var(--color-grey_8_lc)',
    },
};

const TITLE_STYLE_PROPS: TCoreTextStyleProps = {
    ...TextStyle.main,
    textAlign: 'start',
    main: 'var(--color-black)',
    disabled: {
        main: 'var(--color-grey_40_lc)',
    },
};

const SUBTITLE_STYLE_PROPS: TCoreTextStyleProps = {
    ...TextStyle.secondary,
    textAlign: 'start',
    main: 'var(--color-grey_72_lc)',
    margin: { top: 4 },
    disabled: {
        main: 'var(--color-grey_40_lc)',
    },
};

export const SelectorExtraView = ({
    widget,
    uid,
    title,
    subtitle,
    metaText,
    metaVariant,
    metaHierarchy,
    iconName,
    defaultIconName,
    flagIcon,
    flagIconSmall,
    avatar,
    iconHierarchy,
    iconVariant,
    isSelected,
    curIso,
    curIsoIcon,
    margin,
    isDisabled,
    isHide,
    handleClick,
    parentStyleNames = [],
}: TSelectorExtraViewProps) => {
    const resolvedUid = widget?.uid ?? uid ?? '';
    const resolvedTitle = widget?.data?.messages?.title ?? title;
    const resolvedSubtitle = widget?.data?.messages?.subtitle ?? subtitle;
    const resolvedMetaText = widget?.data?.messages?.metaText ?? metaText;
    const resolvedMetaVariant = widget?.options?.metaVariant ?? metaVariant;
    const resolvedMetaHierarchy = widget?.options?.metaHierarchy ?? metaHierarchy;
    const resolvedIconName = widget?.options?.iconName ?? iconName;
    const resolvedFlagIcon = widget?.options?.flagIcon ?? flagIcon;
    const resolvedFlagIconSmall = flagIconSmall;
    const resolvedAvatar = widget?.options?.avatar ?? avatar;
    const resolvedIconHierarchy = widget?.options?.iconHierarchy ?? iconHierarchy;
    const resolvedIconVariant = widget?.options?.iconVariant ?? iconVariant;
    const resolvedIsSelected = widget?.options?.isSelected ?? isSelected;
    const resolvedMargin = widget?.options?.margin ?? margin;

    const resolvedWidget: TCoreButtonViewProps['widget'] =
        (widget as unknown as TCoreButtonViewProps['widget']) ?? ({ uid: resolvedUid, data: {}, options: {} } as TCoreButtonViewProps['widget']);

    const useCurrencyVisual = !!(curIsoIcon && curIso);
    const hasGeneralVisual = !!(resolvedIconName || resolvedFlagIcon || resolvedAvatar);
    const hasVisual = useCurrencyVisual || hasGeneralVisual;

    const renderTrailingMeta = () => {
        if (!resolvedMetaText) {
            return null;
        }
        return (
            <Tag
                metaText={resolvedMetaText}
                metaVariant={resolvedMetaVariant}
                metaHierarchy={resolvedMetaHierarchy}
                isDisabled={isDisabled}
                parentStyleNames={parentStyleNames}
                styleName={resolvedMetaVariant === 'tag' ? 'selectorExtraTagText' : 'selectorExtraMeta'}
            />
        );
    };

    return (
        <CoreButtonView
            widget={resolvedWidget}
            isDisabled={isDisabled}
            isHide={isHide}
            styleProps={{
                ...CONTAINER_STYLE_PROPS,
                padding: { top: hasVisual ? 16 : 24, right: 24, bottom: hasVisual ? 16 : 24, left: 24 },
                margin: resolvedMargin,
            }}
            parentStyleNames={parentStyleNames}
            styleName="selectorExtra"
            onClick={handleClick as MouseEventHandler<HTMLButtonElement>}
        >
            {hasVisual &&
                (useCurrencyVisual ? (
                    <VisualView
                        uid={`icon-${resolvedUid}`}
                        iconName={`ic_${curIso?.toLowerCase()}`}
                        defaultIconName="ic_default_currency"
                        iconHierarchy="primary"
                        iconVariant="circle"
                        size="small"
                        isDisabled={isDisabled}
                        isSelected={resolvedIsSelected}
                        parentStyleNames={[...parentStyleNames, 'selectorExtraCurrencyVisual']}
                    />
                ) : (
                    <VisualView
                        uid={`icon-${resolvedUid}`}
                        iconName={resolvedIconName}
                        defaultIconName={defaultIconName}
                        avatar={resolvedAvatar}
                        flagIcon={resolvedFlagIcon}
                        flagIconSmall={resolvedFlagIconSmall}
                        iconHierarchy={resolvedIconHierarchy}
                        iconVariant={resolvedIconVariant}
                        size="small"
                        isDisabled={isDisabled}
                        isSelected={resolvedIsSelected}
                        parentStyleNames={[...parentStyleNames, 'selectorExtraVisual']}
                    />
                ))}
            <div className={styles.rowContent}>
                {resolvedTitle && (
                    <CoreTextView
                        uid={'title-' + resolvedUid}
                        text={resolvedTitle}
                        as="span"
                        isDisabled={isDisabled}
                        parentStyleNames={parentStyleNames}
                        styleName="selectorExtraTitle"
                        styleProps={TITLE_STYLE_PROPS}
                    />
                )}
                {resolvedSubtitle && (
                    <CoreTextView
                        uid={'subtitle-' + resolvedUid}
                        text={resolvedSubtitle}
                        as="span"
                        isDisabled={isDisabled}
                        parentStyleNames={parentStyleNames}
                        styleName="selectorExtraSubtitle"
                        styleProps={SUBTITLE_STYLE_PROPS}
                    />
                )}
            </div>
            {renderTrailingMeta()}
            {!hasVisual && resolvedIsSelected && (
                <CoreImageView
                    src={getHref('ic_check')}
                    isDisabled={isDisabled}
                    parentStyleNames={parentStyleNames}
                    styleName="selectorExtraCheck"
                    styleProps={{ width: 24, height: 24, iconColor: `var(--color-${isDisabled ? 'purple_40_tr' : 'purple_100'})` }}
                />
            )}
        </CoreButtonView>
    );
};
